import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCitationAnalytics,
  getCitationProbes,
  addCitationProbe,
  deleteCitationProbe,
  triggerCitationSweep,
  getLatestCitationRun,
  getProbeHistory,
  getCitationRecommendations,
  updateCitationRecommendationStatus,
  getAdTestTags,
  addAdTestTag,
  deleteAdTestTag,
  getContent,
  addContent,
  getLinkedInEngagement,
  addLinkedInEngagement,
  getSequences,
} from '../api/measurement';

// ─────────────────────────────────────────────────────────────────────────────
// Citation Analytics
// ─────────────────────────────────────────────────────────────────────────────

export function useCitationAnalytics(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['measurement', 'citations', startDate, endDate],
    queryFn: () => getCitationAnalytics(startDate, endDate),
    refetchInterval: 300_000, // 5 minutes
  });
}

export function useCitationProbes() {
  return useQuery({
    queryKey: ['measurement', 'probes'],
    queryFn: getCitationProbes,
  });
}

export function useAddCitationProbe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addCitationProbe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurement', 'probes'] });
    },
  });
}

export function useDeleteCitationProbe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCitationProbe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurement', 'probes'] });
    },
  });
}

export function useTriggerCitationSweep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerCitationSweep,
    onSuccess: () => {
      // Make the polling hook re-fetch immediately so the UI flips to the
      // running state without waiting for the next interval tick.
      queryClient.invalidateQueries({ queryKey: ['measurement', 'latestRun'] });
    },
  });
}

// Polls the latest run while it's in-flight. Stops polling once the run
// reaches a terminal state. Cache invalidation on terminal transitions is
// handled by the consumer (see FilterBar) so the side-effect fires exactly
// once per state transition instead of on every render.
export function useLatestCitationRun() {
  return useQuery({
    queryKey: ['measurement', 'latestRun'],
    queryFn: () => getLatestCitationRun(),
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status;
      return status === 'running' ? 5_000 : false;
    },
    refetchOnWindowFocus: false,
  });
}

export function useProbeHistory(probeId: string | null) {
  return useQuery({
    queryKey: ['measurement', 'probeHistory', probeId],
    queryFn: () => getProbeHistory(probeId!),
    enabled: !!probeId,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Citation Recommendations
// ─────────────────────────────────────────────────────────────────────────────

export function useCitationRecommendations(status: 'open' | 'done' | 'dismissed' | 'all' = 'open') {
  return useQuery({
    queryKey: ['measurement', 'recommendations', status],
    queryFn: () => getCitationRecommendations(status),
    refetchInterval: 300_000,
  });
}

export function useUpdateRecommendationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'open' | 'done' | 'dismissed' }) =>
      updateCitationRecommendationStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurement', 'recommendations'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Ad Test Tags
// ─────────────────────────────────────────────────────────────────────────────

export function useAdTestTags() {
  return useQuery({
    queryKey: ['measurement', 'adTestTags'],
    queryFn: getAdTestTags,
  });
}

export function useAddAdTestTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addAdTestTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurement', 'adTestTags'] });
    },
  });
}

export function useDeleteAdTestTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAdTestTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurement', 'adTestTags'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Content
// ─────────────────────────────────────────────────────────────────────────────

export function useContent() {
  return useQuery({
    queryKey: ['measurement', 'content'],
    queryFn: getContent,
  });
}

export function useAddContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurement', 'content'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// LinkedIn
// ─────────────────────────────────────────────────────────────────────────────

export function useLinkedInEngagement(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['measurement', 'linkedin', startDate, endDate],
    queryFn: () => getLinkedInEngagement(startDate, endDate),
  });
}

export function useAddLinkedInEngagement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addLinkedInEngagement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurement', 'linkedin'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Sequences
// ─────────────────────────────────────────────────────────────────────────────

export function useSequences() {
  return useQuery({
    queryKey: ['measurement', 'sequences'],
    queryFn: getSequences,
  });
}
